// preview 專用：電子資源瀏覽器（Phase 1）。
// 資料庫過濾 pills ＋ 跨庫書名/作者搜尋 ＋ 結果卡（帶直連）。讀 eresource_data.js。
// trialOnly=true 時只顯示試用資料庫；initialKeyword 由查詢頁 tab/search 帶入。
import { useMemo, useState } from "react";
import { ERESOURCES } from "./eresource_data";
import { Search, ExternalLink, Database } from "lucide-react";

const typeColor = {
  電子書: "#2563eb",
  電子期刊: "#7c3aed",
  電子雜誌: "#db2777",
  學位論文: "#059669",
};

const EResourceBrowser = ({ initialKeyword = "", trialOnly = false }) => {
  const dbs = useMemo(
    () => (trialOnly ? ERESOURCES.filter((d) => d.trial) : ERESOURCES),
    [trialOnly]
  );
  const [kw, setKw] = useState(initialKeyword);
  const [activeDb, setActiveDb] = useState("all");

  const results = useMemo(() => {
    const pool = activeDb === "all" ? dbs : dbs.filter((d) => d.key === activeDb);
    const q = kw.trim().toLowerCase();
    const out = [];
    pool.forEach((db) => {
      (db.items || []).forEach((it) => {
        const hay = `${it.title} ${it.author} ${it.note}`.toLowerCase();
        if (!q || hay.includes(q)) out.push({ ...it, _db: db });
      });
    });
    return out;
  }, [kw, activeDb, dbs]);

  if (trialOnly && dbs.length === 0) {
    return (
      <div style={{ background: "#fff", borderRadius: "14px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", padding: "2.5rem 1.5rem", textAlign: "center", color: "#64748b" }}>
        試用資料庫清單待業主指定（可於後台標記哪些資料庫為「試用」）。
      </div>
    );
  }

  const pill = (key, label, count, on) => (
    <button
      key={key}
      type="button"
      onClick={() => setActiveDb(key)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        padding: "0.4rem 0.9rem",
        borderRadius: "999px",
        border: on ? "none" : "1px solid #c7d2fe",
        background: on ? "linear-gradient(90deg,#1e3a8a,#2563eb)" : "#fff",
        color: on ? "#fff" : "#1e3a8a",
        fontWeight: 600,
        fontSize: "0.85rem",
        cursor: "pointer",
      }}
    >
      {label}
      {count != null && (
        <span
          style={{
            background: on ? "rgba(255,255,255,0.25)" : "#eef2ff",
            color: on ? "#fff" : "#4f46e5",
            borderRadius: "999px",
            padding: "0 7px",
            fontSize: "0.72rem",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div style={{ background: "#fff", borderRadius: "14px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", padding: "1.25rem" }}>
      {/* 搜尋 */}
      <div style={{ position: "relative", marginBottom: "1rem" }}>
        <Search size={18} color="#94a3b8" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
        <input
          value={kw}
          onChange={(e) => setKw(e.target.value)}
          placeholder="搜尋電子資源書名 / 作者（跨資料庫）"
          style={{
            width: "100%",
            padding: "0.7rem 1rem 0.7rem 2.6rem",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            fontSize: "0.95rem",
            outline: "none",
          }}
        />
      </div>

      {/* 資料庫 pills */}
      <div style={{ display: "flex", gap: "9px", flexWrap: "wrap", marginBottom: "1rem" }}>
        {pill("all", "全部", null, activeDb === "all")}
        {dbs.map((d) => pill(d.key, d.name, d.total, activeDb === d.key))}
      </div>

      {/* 結果數 */}
      <div style={{ color: "#64748b", fontSize: "0.82rem", marginBottom: "0.6rem" }}>
        顯示 {results.length} 筆{kw.trim() ? `（關鍵字「${kw.trim()}」）` : "（preview 取樣）"}
      </div>

      {/* 結果卡 */}
      {results.length === 0 ? (
        <div style={{ color: "#94a3b8", textAlign: "center", padding: "2rem 0" }}>查無符合的電子資源</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "10px", maxHeight: "560px", overflowY: "auto", paddingRight: "4px" }}>
          {results.slice(0, 300).map((r, i) => {
            const href = r.url || r._db.vendor;
            const color = typeColor[r._db.type] || "#2563eb";
            return (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "none",
                  display: "block",
                  background: "#f8fafc",
                  borderRadius: "10px",
                  borderLeft: `4px solid ${color}`,
                  padding: "0.7rem 0.9rem",
                  transition: "background 0.2s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "#eef2ff")}
                onMouseOut={(e) => (e.currentTarget.style.background = "#f8fafc")}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                  <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.9rem", lineHeight: 1.4 }}>{r.title}</span>
                  <ExternalLink size={14} color="#94a3b8" style={{ flexShrink: 0, marginTop: "3px" }} />
                </div>
                <div style={{ color: "#64748b", fontSize: "0.78rem", marginTop: "3px" }}>
                  {[r.author, r.year, r.note].filter(Boolean).join(" · ")}
                </div>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", marginTop: "6px", fontSize: "0.7rem", fontWeight: 600, color }}>
                  <Database size={11} /> {r._db.name}
                </span>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EResourceBrowser;
